import CustomButton from "@components/buttons/CustomButton";
import { MerchantDropdown } from "@components/dropdowns/MerchantDropdown";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { analyticsApis } from "@services/apis/Analytics";
import {
  GetCashierTradeVolumeData,
  GetCashierTradeVolumeRequest,
} from "@services/models/Groups/GetCashierTradeVolumeModel";
import { GetGroupsData } from "@services/models/Groups/GetGroups";
import { format, isValid } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import { DateRange } from "react-date-range";
import Skeleton from "react-loading-skeleton";
import ConstantFunctions from "../../../utils/constants_functions";
import { CSV_EXPORT_CONSTANTS } from "../../../utils/csvExportConstants";

interface RangeType {
  startDate: Date;
  endDate: Date;
  key: string;
}

interface GroupPerformanceSliderProps {
  isOpen: boolean;
  onClose: () => void;
  groups: GetGroupsData[];
}

type DateFilter = "daily" | "monthly";

const GroupPerformanceSlider: React.FC<GroupPerformanceSliderProps> = ({
  isOpen,
  onClose,
  groups,
}) => {
  const columnWidths = {
    date: "20%",
    cashierId: "20%",
    username: "30%",
    tpvUsd: "30%",
    totalTrades: "15%",
  };

  const constantFunctions = new ConstantFunctions();
  const [selectedGroupId, setSelectedGroupId] = useState<number>(groups[0]?.id);
  const [dateFilter, setDateFilter] = useState<DateFilter>("daily");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [cashierVolume, setCashierVolume] = useState<
    GetCashierTradeVolumeData[]
  >([]);
  const [totalTpvUsd, setTotalTpvUsd] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<RangeType[]>([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);
  const [customDateMode, setCustomDateMode] = useState<boolean>(false);
  const [lastAppliedAt, setLastAppliedAt] = useState<number>(0);
  const datePickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setShowDatePicker(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [datePickerRef]);

  const getCurrentMonth = () => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const now = new Date();
    return `${months[now.getMonth()]} ${now.getFullYear()}`;
  };

  const handleGroupSelection = async (value: string | number) => {
    const selectedId = Number(value);
    setSelectedGroupId(selectedId);
    setCashierVolume([]);
  };

  const handleDateFilterChange = (filter: DateFilter) => {
    setDateFilter(filter);
    setCustomDateMode(false);
    setLastAppliedAt(Date.now()); // Trigger API call
    setShowDatePicker(false);
  };

  const handleDateRangeChange = (ranges: any) => {
    setDateRange([ranges.selection]);
    // Don't call API here, only update the date range state
  };

  const handleCustomDateSelect = () => {
    if (dateRange[0].startDate && dateRange[0].endDate) {
      setCustomDateMode(true);
      setLastAppliedAt(Date.now()); // Trigger API call only when Apply is clicked
      setShowDatePicker(false);
    }
  };

  const toggleDatePicker = () => {
    setShowDatePicker(!showDatePicker);
    if (customDateMode && !showDatePicker) {
      setShowDatePicker(true);
    }
  };

  useEffect(() => {
    setSelectedGroupId(groups[0]?.id);
  }, [groups]);

  useEffect(() => {
    const today = new Date();
    setDateRange([
      {
        startDate: today,
        endDate: today,
        key: "selection",
      },
    ]);
  }, []);
  const getDateRange = useCallback(() => {
    if (customDateMode && dateRange[0].startDate && dateRange[0].endDate) {
      const startDate = format(dateRange[0].startDate, "yyyy-MM-dd");
      const endDate = format(dateRange[0].endDate, "yyyy-MM-dd");
      return { startDate, endDate };
    }

    const now = new Date();

    if (dateFilter === "daily") {
      const today = format(now, "yyyy-MM-dd");
      return { startDate: today, endDate: today };
    } else {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return {
        startDate: format(firstDay, "yyyy-MM-dd"),
        endDate: format(lastDay, "yyyy-MM-dd"),
      };
    }
  }, [dateFilter, customDateMode, dateRange]);
  const getCashierVolume = useCallback(async () => {
    if (!selectedGroupId) return;

    try {
      setIsLoading(true);
      setError(null);

      const { startDate, endDate } = getDateRange();

      const payload: GetCashierTradeVolumeRequest = {
        groupId: selectedGroupId,
        startDate,
        endDate,
        page: 1,
        rows: 1000,
      };

      const response = await analyticsApis.getCashierTradeVolume(payload);
      setCashierVolume(response.data);

      // Calculate total TPV USD
      const total = response.data.reduce(
        (sum, item) => sum + (item.successfulTradeVolumeUSD || 0),
        0,
      );
      setTotalTpvUsd(total);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg || error.msg || "Something went wrong";
      setError(errorMessage);
      setCashierVolume([]);
      setTotalTpvUsd(0);
    } finally {
      setIsLoading(false);
    }
  }, [selectedGroupId, getDateRange]);
  const exportCashierDataToCsv = async () => {
    try {
      if (!selectedGroupId) {
        setError("Please select a group first");
        return;
      }

      setExportLoading(true);
      const { startDate, endDate } = getDateRange();
      const { MAX_ROWS, ROWS_PER_PAGE, TOTAL_PAGES } = CSV_EXPORT_CONSTANTS;
      let allCashierData: GetCashierTradeVolumeData[] = [];

      for (let i = 1; i <= TOTAL_PAGES; i++) {
        const payload: GetCashierTradeVolumeRequest = {
          groupId: selectedGroupId,
          startDate,
          endDate,
          page: i,
          rows: ROWS_PER_PAGE,
        };

        const response = await analyticsApis.getCashierTradeVolume(payload);
        allCashierData = [...allCashierData, ...response.data];

        if (
          response.data.length < ROWS_PER_PAGE ||
          allCashierData.length >= MAX_ROWS
        ) {
          break;
        }
      }

      allCashierData = allCashierData.slice(0, MAX_ROWS);

      const headers = [
        "Date",
        "Cashier UID",
        "Username",
        "TPV (USD)",
        "Total Trades",
      ];

      let csvContent = headers.join(",") + "\n";

      allCashierData.forEach((item) => {
        const row = [
          `"${
            customDateMode
              ? constantFunctions.dateFormatInDDMMYY(item.dateRangeStart)
              : dateFilter === "monthly"
                ? getCurrentMonth()
                : constantFunctions.dateFormatInDDMMYY(item.dateRangeStart)
          }"`,
          item.userId,
          `"${(item.username || "").replace(/"/g, '""')}"`,
          constantFunctions.formatCurrency(item.successfulTradeVolumeUSD),
          item.totalTrades,
        ];

        csvContent += row.join(",") + "\n";
      });

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);

      const date = new Date();
      const formattedDate = `${date.getFullYear()}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

      const selectedGroup = groups.find((g) => g.id === selectedGroupId);
      const groupName = selectedGroup
        ? selectedGroup.name.replace(/\s+/g, "-").toLowerCase()
        : "group";

      link.setAttribute(
        "download",
        `${groupName}-cashier-performance-${formattedDate}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error: any) {
      console.error("Failed to export cashier data", error);
      setError(
        "Failed to export cashier data: " + (error?.message || "Unknown error"),
      );
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    if (selectedGroupId) {
      getCashierVolume();
    }
  }, [
    selectedGroupId,
    dateFilter,
    customDateMode,
    lastAppliedAt,
    getCashierVolume,
  ]);

  return (
    <SlidingPanel
      isOpen={isOpen}
      heading={"Group Performance"}
      widthDesktop={"35%"}
      onClose={onClose}
      onClickCross={onClose}
      isSlider={true}
      showConfirm={false}
    >
      <div className="flex flex-col px-[16px] py-[16px] gap-4 mt-[54px] pb-[60px]">
        <div className="flex flex-col gap-3">
          <MerchantDropdown
            listOfValues={groups.map((group) => ({
              id: group.id,
              name: group.name,
            }))}
            value={selectedGroupId ?? undefined}
            onChange={handleGroupSelection}
            label="Select Group"
            color="bg-page-body"
            expandedBorderColor="border-button-primary"
            baseColor="bg-page-body"
            borderColor="border-subtle"
          />

          <div className="flex flex-row items-center">
            <div className="relative">
              <div className="flex rounded-full overflow-hidden border border-border-subtle">
                <button
                  className={`px-4 py-1 text-[10px] ${
                    dateFilter === "daily" && !customDateMode
                      ? "bg-bg-button-secondary border-border-button-secondary"
                      : "bg-bg-page-body"
                  }`}
                  onClick={() => handleDateFilterChange("daily")}
                >
                  Daily
                </button>
                <button
                  className={`px-4 py-1 text-[10px] ${
                    dateFilter === "monthly" && !customDateMode
                      ? "bg-bg-button-secondary border-border-button-secondary"
                      : "bg-bg-page-body"
                  }`}
                  onClick={() => handleDateFilterChange("monthly")}
                >
                  Monthly
                </button>
                <button
                  className={`px-4 py-1 text-[10px] flex items-center gap-1 ${
                    customDateMode
                      ? "bg-bg-button-secondary border-border-button-secondary"
                      : "bg-bg-page-body"
                  }`}
                  onClick={toggleDatePicker}
                >
                  <span>Custom</span>
                  <span className="material-symbols-outlined text-[12px]">
                    calendar_month
                  </span>
                </button>
              </div>

              {showDatePicker && (
                <div
                  ref={datePickerRef}
                  className="absolute top-8 left-0 z-20 bg-bg-page-body shadow-lg rounded-lg p-2 border border-border-subtle"
                >
                  <DateRange
                    editableDateInputs={false}
                    showDateDisplay={false}
                    onChange={handleDateRangeChange}
                    moveRangeOnFirstSelection={false}
                    ranges={dateRange}
                    color="#D2E9F3"
                  />
                  <div className="flex justify-center mt-1">
                    <CustomButton
                      text="APPLY"
                      onClick={handleCustomDateSelect}
                      isActive={true}
                      className="w-full text-[10px] py-1"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="text-[12px] text-text-status-red bg-red-50 p-2 rounded">
            {error}
          </div>
        )}

        <div className="flex flex-col">
          <div className="flex flex-row justify-between items-center mb-[8px]">
            <div className="flex items-center gap-2">
              <span className="text-[12px] font-semibold">
                Cashier Performance (
                {customDateMode
                  ? `${
                      isValid(dateRange[0].startDate)
                        ? format(dateRange[0].startDate, "dd/MM/yyyy")
                        : ""
                    } - ${
                      isValid(dateRange[0].endDate)
                        ? format(dateRange[0].endDate, "dd/MM/yyyy")
                        : ""
                    }`
                  : dateFilter === "daily"
                    ? "Today"
                    : "This Month"}
                )
              </span>
              <div
                className="cursor-pointer flex items-center"
                onClick={exportCashierDataToCsv}
              >
                <span
                  className={`material-symbols-outlined ${
                    exportLoading ? "animate-spin" : ""
                  } text-[18px] text-text-button-tertiary hover:bg-bg-button-secondary rounded p-1`}
                >
                  {exportLoading ? "progress_activity" : "download"}
                </span>
              </div>
            </div>
          </div>

          <div className="w-full mb-3">
            <GlobalTextInputField
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              type="text"
              placeholder="Search by UID, Username or Email"
              prefixIcon={
                <span className="material-symbols-outlined text-text-subtle text-[18px]">
                  search
                </span>
              }
            />
          </div>

          <div className="flex flex-row bg-bg-base px-[8px] py-[8px] rounded-t-[16px]">
            <span
              className="font-semibold text-[10px]"
              style={{ width: columnWidths.date }}
            >
              Date
            </span>
            <span
              className="font-semibold text-[10px]"
              style={{ width: columnWidths.cashierId }}
            >
              Cashier UID
            </span>
            <span
              className="font-semibold text-[10px]"
              style={{ width: columnWidths.username }}
            >
              Username
            </span>
            <span
              className="font-semibold text-[10px]"
              style={{ width: columnWidths.tpvUsd }}
            >
              TPV (USD)
            </span>
            <span
              className="font-semibold text-[10px]"
              style={{ width: columnWidths.totalTrades }}
            >
              Total Trades
            </span>
          </div>

          <div className="h-[500px] overflow-y-auto overflow-x-hidden border-r border-l border-b border-border-subtle rounded-b-[16px]">
            {isLoading ? (
              <>
                {[...Array(10)].map((_, index) => (
                  <div
                    key={`loading-${index}`}
                    className="flex flex-row px-[8px] py-[8px] border-b border-border-table"
                  >
                    {Object.keys(columnWidths).map((column, i) => (
                      <div
                        key={i}
                        style={{
                          width:
                            columnWidths[column as keyof typeof columnWidths],
                        }}
                      >
                        <Skeleton height={15} />
                      </div>
                    ))}
                  </div>
                ))}
              </>
            ) : cashierVolume.length === 0 ? (
              <div className="text-center py-4 text-gray-500 text-[10px]">
                No data found
              </div>
            ) : (
              (() => {
                const filteredData = cashierVolume.filter((item) => {
                  if (!searchTerm) return true;
                  const search = searchTerm.toLowerCase();
                  return (
                    (item.userId && item.userId.toString().includes(search)) ||
                    (item.username &&
                      item.username.toLowerCase().includes(search)) ||
                    (item.emailId &&
                      item.emailId?.toLowerCase().includes(search))
                  );
                });

                if (filteredData.length === 0) {
                  return (
                    <div className="text-center py-4 text-gray-500 text-[10px]">
                      No matching cashiers found
                    </div>
                  );
                }

                return filteredData.map((item, index) => (
                  <div
                    key={`cashier-${item.userId}-${index}`}
                    className={`${
                      index === filteredData.length - 1
                        ? ""
                        : "border-b border-border-subtle"
                    } px-[8px] py-[8px] flex flex-row`}
                  >
                    <span
                      className="text-[10px]"
                      style={{ width: columnWidths.date }}
                    >
                      {customDateMode
                        ? constantFunctions.dateFormatInDDMMYY(
                            item.dateRangeStart,
                          )
                        : dateFilter === "monthly"
                          ? getCurrentMonth()
                          : constantFunctions.dateFormatInDDMMYY(
                              item.dateRangeStart,
                            )}
                    </span>
                    <span
                      className="text-[10px]"
                      style={{ width: columnWidths.cashierId }}
                    >
                      {item.userId}
                    </span>
                    <span
                      className="text-[10px]"
                      style={{ width: columnWidths.username }}
                    >
                      {item.username}
                    </span>
                    <span
                      className="text-[10px]"
                      style={{ width: columnWidths.tpvUsd }}
                    >
                      $
                      {constantFunctions.formatCurrency(
                        item.successfulTradeVolumeUSD,
                      )}
                    </span>
                    <span
                      className="text-[10px]"
                      style={{ width: columnWidths.totalTrades }}
                    >
                      {item.totalTrades}
                    </span>
                  </div>
                ));
              })()
            )}
          </div>
        </div>

        <div className="fixed bottom-0 left-0 w-full bg-bg-base shadow-lg border-t border-border-subtle py-3 px-4 z-10">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-[12px]">Total TPV (USD)</span>
            <span className="font-semibold text-[16px] text-text-base">
              ${constantFunctions.formatCurrency(totalTpvUsd)}
            </span>
          </div>
        </div>
      </div>
    </SlidingPanel>
  );
};

export default GroupPerformanceSlider;
